import React, { useState, useCallback, useRef } from 'react';
import { useAuth } from '../../authentication_components/authContext';
import { useBusinessData } from '../../authentication_components/businessDataContext';
import { useBusinessProduct } from '../../authentication_components/businessProductContext';
import { useBusinessEmployee } from '../../authentication_components/businessEmployeeContext';
import { useBusinessService } from '../../authentication_components/businessServiceContext';
import { useBusinessEvent } from '../../authentication_components/businessEventContext';
import { useBusinessGallery } from '../../authentication_components/businessGalleryContext';
import { useEventImages } from '../../authentication_components/eventImagesContext';
import { useAdminTracker } from '../../authentication_components/adminTrackerContext';

// Icons needed for the Parent UI (Search, Header, Navigation)
import {
  Search, Building, User, Phone, Mail, Calendar, MapPin,
  Tag, Package, Wrench, Users, PartyPopper, ChevronDown,
  ChevronRight, Camera, Loader2, Images, X, Upload, Trash2,
  GripVertical, ImagePlus, Check
} from 'lucide-react';

// Placeholder imports for the sub-components we will build next
import BusinessGallerySection from './business_content_subcomponents/business_gallery_section';
import BusinessProductsSection from './business_content_subcomponents/business_products_section';
import BusinessServicesSection from './business_content_subcomponents/business_services_section';
import BusinessEmployeesSection from './business_content_subcomponents/business_employees_section';
import BusinessEventsSection from './business_content_subcomponents/business_events_section';

const BusinessContent = () => {
  // ============================================================
  // CONTEXTS & HOOKS
  // ============================================================
  const { adminId } = useAuth();
  const businessData = useBusinessData();
  const businessProduct = useBusinessProduct();
  const businessEmployee = useBusinessEmployee();
  const businessService = useBusinessService();
  const businessEvent = useBusinessEvent();
  const businessGallery = useBusinessGallery();
  const eventImages = useEventImages();
  const { createTracker } = useAdminTracker();

  // ============================================================
  // LOCAL STATE
  // ============================================================
  // Search & Visibility
  const [registryNumber, setRegistryNumber] = useState('');
  const [searchedBusiness, setSearchedBusiness] = useState(null);
  const [businessNotFound, setBusinessNotFound] = useState(false);
  const [isSearching, setIsSearching] = useState(false);
  const [activeSection, setActiveSection] = useState(null);

  // Header / Logo State
  const [logoUrl, setLogoUrl] = useState(null);
  const [isUploadingImage, setIsUploadingImage] = useState(false);
  const fileInputRef = useRef(null);

  // Data Arrays (Fetched here, passed down as props)
  const [businessProducts, setBusinessProducts] = useState([]);
  const [businessEmployees, setBusinessEmployees] = useState([]);
  const [businessServices, setBusinessServices] = useState([]);
  const [businessEvents, setBusinessEvents] = useState([]);

  // Event Images State
  const [eventImagesMap, setEventImagesMap] = useState({});
  const [selectedEventForImages, setSelectedEventForImages] = useState(null);
  const [showEventImageDialog, setShowEventImageDialog] = useState(false);
  const [currentEventImages, setCurrentEventImages] = useState([]);
  const [isLoadingEventImages, setIsLoadingEventImages] = useState(false);
  const [draggedImageId, setDraggedImageId] = useState(null);
  const eventImageInputRef = useRef(null);

  // ============================================================
  // DATA LOADING LOGIC
  // ============================================================

  // Load Business Logo (Header Logic)
  const loadBusinessLogo = useCallback(async (regNum) => {
    try {
      const logoData = await businessData.getBusinessLogo(regNum);
      if (logoData && logoData.has_logo) {
        setLogoUrl(logoData.url);
      } else {
        setLogoUrl(null);
      }
    } catch (e) {
      console.error("Error loading logo", e);
      setLogoUrl(null);
    }
  }, [businessData]);

  // Load event images for a specific event
  const loadEventImages = useCallback(async (eventId) => {
    try {
      const result = await eventImages.getEventImages(eventId);
      return result.images || [];
    } catch (error) {
      console.error('Error loading event images:', error);
      return [];
    }
  }, [eventImages]);

  // Load primary images for all events
  const loadEventPrimaryImages = useCallback(async (events) => {
    const imagesMap = {};

    await Promise.all(
      events.map(async (event) => {
        try {
          const images = await loadEventImages(event.event_id);
          if (images.length > 0) {
            imagesMap[event.event_id] = images[0]; // First image is primary
          }
        } catch (error) {
          console.error(`Error loading images for event ${event.event_id}:`, error);
        }
      })
    );

    setEventImagesMap(imagesMap);
  }, [loadEventImages]);

  // Load All Sub-Data (Products, Services, Employees, Events)
  const loadBusinessData = useCallback(async (registryNum) => {
    try {
      const [products, employees, services, events] = await Promise.all([
        businessProduct.getProductsByRegistryNumber(registryNum, { limit: 100 }).catch(() => ({ products: [] })),
        businessEmployee.getEmployeesByRegistryNumber(registryNum, { limit: 100 }).catch(() => ({ employees: [] })),
        businessService.getServicesByRegistryNumber(registryNum, { limit: 100 }).catch(() => ({ services: [] })),
        businessEvent.getEventsByRegistryNumber(registryNum, { limit: 100 }).catch(() => ({ events: [] })),
      ]);

      const eventsList = events.events || [];
      setBusinessProducts(products.products || []);
      setBusinessEmployees(employees.employees || []);
      setBusinessServices(services.services || []);
      setBusinessEvents(eventsList);

      // Load primary images for all events
      if (eventsList.length > 0) {
        await loadEventPrimaryImages(eventsList);
      }
    } catch (error) {
      console.error('Error loading business data:', error);
      setBusinessProducts([]);
      setBusinessEmployees([]);
      setBusinessServices([]);
      setBusinessEvents([]);
      setEventImagesMap({});
    }
  }, [businessProduct, businessEmployee, businessService, businessEvent, loadEventPrimaryImages]);

  // Main Search Handler
  const handleSearch = useCallback(async () => {
    if (!registryNumber.trim()) return;

    setIsSearching(true);
    setActiveSection(null);
    setBusinessNotFound(false);
    setSearchedBusiness(null);
    setLogoUrl(null);

    // Clear data lists on new search
    setBusinessProducts([]);
    setBusinessServices([]);
    setBusinessEmployees([]);
    setBusinessEvents([]);
    setEventImagesMap({});

    try {
      const result = await businessData.getBusinessByRegistryNumber(registryNumber.trim());
      if (result) {
        setSearchedBusiness(result);
        setBusinessNotFound(false);

        // Parallel load of secondary data
        await Promise.all([
          loadBusinessData(result.business_registry_number),
          loadBusinessLogo(result.business_registry_number)
        ]);
      }
    } catch (error) {
      console.error('Error searching business:', error);
      setSearchedBusiness(null);
      setBusinessNotFound(true);
    } finally {
      setIsSearching(false);
    }
  }, [registryNumber, businessData, loadBusinessData, loadBusinessLogo]);

  // ============================================================
  // EVENT IMAGE MANAGEMENT
  // ============================================================

  const openEventImageDialog = async (event) => {
    setSelectedEventForImages(event);
    setShowEventImageDialog(true);
    setIsLoadingEventImages(true);

    try {
      const images = await loadEventImages(event.event_id);
      setCurrentEventImages(images);
    } catch (error) {
      console.error('Error loading event images:', error);
      setCurrentEventImages([]);
    } finally {
      setIsLoadingEventImages(false);
    }
  };

  const closeEventImageDialog = () => {
    setShowEventImageDialog(false);
    setSelectedEventForImages(null);
    setCurrentEventImages([]);
    if (eventImageInputRef.current) {
      eventImageInputRef.current.value = '';
    }
  };

  const handleEventImageUpload = async (event) => {
    const files = event.target.files;
    if (!files || files.length === 0 || !selectedEventForImages) return;

    const file = files[0];

    // Validate file size (15MB limit)
    if (file.size > 15 * 1024 * 1024) {
      alert('El archivo es demasiado grande. El máximo es 15MB.');
      if (eventImageInputRef.current) {
        eventImageInputRef.current.value = '';
      }
      return;
    }

    try {
      await eventImages.uploadEventImage(selectedEventForImages.event_id, file);

      // Reload images for this event
      const updatedImages = await loadEventImages(selectedEventForImages.event_id);
      setCurrentEventImages(updatedImages);

      // Update the primary image in the map
      if (updatedImages.length > 0) {
        setEventImagesMap(prev => ({
          ...prev,
          [selectedEventForImages.event_id]: updatedImages[0]
        }));
      }

      // Track the change
      try {
        await createTracker({
          admin_id: adminId || 'unknown',
          change_type: 'event_image_upload',
          change_reason: `Event "${selectedEventForImages.event_name}" (${selectedEventForImages.event_id}) - Image uploaded: ${file.name}`
        });
      } catch (trackerErr) {
        console.error('[AdminTracker] Failed to track event image upload:', trackerErr);
      }

    } catch (error) {
      console.error('Error uploading event image:', error);
      alert('Error al subir la imagen: ' + (error.message || 'Error desconocido'));
    } finally {
      if (eventImageInputRef.current) {
        eventImageInputRef.current.value = '';
      }
    }
  };

  const handleEventImageDelete = async (imageId) => {
    if (!selectedEventForImages) return;

    if (!window.confirm('¿Está seguro de que desea eliminar esta imagen?')) {
      return;
    }

    try {
      await eventImages.deleteEventImage(selectedEventForImages.event_id, imageId);

      // Reload images for this event
      const updatedImages = await loadEventImages(selectedEventForImages.event_id);
      setCurrentEventImages(updatedImages);

      // Update the primary image in the map
      if (updatedImages.length > 0) {
        setEventImagesMap(prev => ({
          ...prev,
          [selectedEventForImages.event_id]: updatedImages[0]
        }));
      } else {
        // No images left, remove from map
        setEventImagesMap(prev => {
          const newMap = { ...prev };
          delete newMap[selectedEventForImages.event_id];
          return newMap;
        });
      }

      // Track the change
      try {
        await createTracker({
          admin_id: adminId || 'unknown',
          change_type: 'event_image_delete',
          change_reason: `Event "${selectedEventForImages.event_name}" (${selectedEventForImages.event_id}) - Image deleted: ${imageId}`
        });
      } catch (trackerErr) {
        console.error('[AdminTracker] Failed to track event image deletion:', trackerErr);
      }

    } catch (error) {
      console.error('Error deleting event image:', error);
      alert('Error al eliminar la imagen: ' + (error.message || 'Error desconocido'));
    }
  };

  const handleDragStart = (imageId) => {
    setDraggedImageId(imageId);
  };

  const handleDragOver = (e) => {
    e.preventDefault();
  };

  const handleDrop = async (e, targetImageId) => {
    e.preventDefault();

    if (!draggedImageId || !selectedEventForImages || draggedImageId === targetImageId) {
      setDraggedImageId(null);
      return;
    }

    try {
      // Get current order
      const currentOrder = currentEventImages.map(img => img.cloudflare_image_id);

      // Find indices
      const draggedIndex = currentOrder.indexOf(draggedImageId);
      const targetIndex = currentOrder.indexOf(targetImageId);

      // Reorder array
      const newOrder = [...currentOrder];
      const [removed] = newOrder.splice(draggedIndex, 1);
      newOrder.splice(targetIndex, 0, removed);

      // Update via API
      await eventImages.reorderEventImages(selectedEventForImages.event_id, newOrder);

      // Reload images to reflect new order
      const updatedImages = await loadEventImages(selectedEventForImages.event_id);
      setCurrentEventImages(updatedImages);

      // Update primary image in map
      if (updatedImages.length > 0) {
        setEventImagesMap(prev => ({
          ...prev,
          [selectedEventForImages.event_id]: updatedImages[0]
        }));
      }

      // Track the change
      try {
        await createTracker({
          admin_id: adminId || 'unknown',
          change_type: 'event_image_reorder',
          change_reason: `Event "${selectedEventForImages.event_name}" (${selectedEventForImages.event_id}) - Images reordered`
        });
      } catch (trackerErr) {
        console.error('[AdminTracker] Failed to track event image reorder:', trackerErr);
      }

    } catch (error) {
      console.error('Error reordering images:', error);
      alert('Error al reordenar imágenes: ' + (error.message || 'Error desconocido'));
    } finally {
      setDraggedImageId(null);
    }
  };

  const handleSetAsPrimary = async (imageId) => {
    if (!selectedEventForImages) return;

    try {
      await eventImages.setAsPrimaryEventImage(selectedEventForImages.event_id, imageId);

      // Reload images to reflect new order
      const updatedImages = await loadEventImages(selectedEventForImages.event_id);
      setCurrentEventImages(updatedImages);

      // Update primary image in map
      if (updatedImages.length > 0) {
        setEventImagesMap(prev => ({
          ...prev,
          [selectedEventForImages.event_id]: updatedImages[0]
        }));
      }

      // Track the change
      try {
        await createTracker({
          admin_id: adminId || 'unknown',
          change_type: 'event_primary_image_change',
          change_reason: `Event "${selectedEventForImages.event_name}" (${selectedEventForImages.event_id}) - Primary image changed: ${imageId}`
        });
      } catch (trackerErr) {
        console.error('[AdminTracker] Failed to track primary image change:', trackerErr);
      }

    } catch (error) {
      console.error('Error setting primary image:', error);
      alert('Error al establecer imagen principal: ' + (error.message || 'Error desconocido'));
    }
  };

  // ============================================================
  // HEADER / LOGO LOGIC
  // ============================================================
  const handleImageChange = async (event) => {
    if (!event.target.files || event.target.files.length === 0) return;
    const file = event.target.files[0];
    if (!searchedBusiness) return;

    if (file.size > 5 * 1024 * 1024) {
      alert("El archivo es demasiado grande. El máximo es 5MB.");
      if (fileInputRef.current) fileInputRef.current.value = '';
      return;
    }

    const formData = new FormData();
    formData.append('image', file);
    const hadPreviousLogo = !!logoUrl;

    setIsUploadingImage(true);
    try {
      const result = await businessData.updateBusinessLogo(searchedBusiness.business_registry_number, formData);
      if (result && result.url) {
        setLogoUrl(result.url);
      } else {
        loadBusinessLogo(searchedBusiness.business_registry_number);
      }

      // Track the change
      try {
        const changeDescription = hadPreviousLogo
          ? `Profile photo updated (replaced existing image)`
          : `Profile photo added (first image upload)`;

        await createTracker({
          admin_id: adminId || 'unknown',
          change_type: 'profile_photo_change',
          change_reason: `Business ${searchedBusiness.business_registry_number} (${searchedBusiness.business_name}) - ${changeDescription}. File: ${file.name}`
        });
      } catch (trackerErr) {
        console.error('[AdminTracker] Failed to track profile photo change:', trackerErr);
      }
    } catch (error) {
      console.error('Error uploading image:', error);
      alert('Error al actualizar la imagen: ' + (error.message || 'Error desconocido'));
    } finally {
      setIsUploadingImage(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  const triggerFileInput = () => {
    if (fileInputRef.current) fileInputRef.current.click();
  };

  // ============================================================
  // UI HELPER
  // ============================================================
  const handleSectionClick = (sectionName) => {
    setActiveSection(activeSection === sectionName ? null : sectionName);
  };

  const getSectionCount = (sectionData) => {
    return sectionData ? sectionData.length : 0;
  };

  // Render the accordion button
  const renderSectionHeader = (icon, title, count, sectionName, isGallery = false) => (
    <button
      onClick={() => handleSectionClick(sectionName)}
      className="w-full flex items-center justify-between p-4 bg-white border border-orange-200 rounded-lg hover:bg-orange-50 transition-all duration-200 shadow-sm"
    >
      <div className="flex items-center space-x-3">
        {icon}
        <span className="font-medium text-gray-800">{title}</span>
        {!isGallery && (
          <span className="text-sm text-orange-600 bg-orange-100 px-2 py-1 rounded-full">
            {count}x
          </span>
        )}
      </div>
      <div className="flex items-center space-x-2">
        {activeSection === sectionName ? (
          <ChevronDown className="h-5 w-5 text-orange-500" />
        ) : (
          <ChevronRight className="h-5 w-5 text-gray-400" />
        )}
      </div>
    </button>
  );

  return (
    <div className="bg-gradient-to-br from-orange-50 to-orange-100 min-h-screen">

      {/* 1. SEARCH HEADER */}
      <div className="bg-white border-b border-orange-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex gap-4 max-w-2xl">
            <div className="relative flex-1">
              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                <Building className="h-5 w-5 text-orange-400" />
              </div>
              <input
                type="text"
                placeholder="Ingrese el número de registro del negocio (ej: REG123456)"
                value={registryNumber}
                onChange={(e) => setRegistryNumber(e.target.value)}
                onKeyPress={(e) => e.key === 'Enter' && handleSearch()}
                className="w-full pl-10 pr-10 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-orange-500 hover:border-orange-300 transition-all duration-200"
              />
              {registryNumber && (
                <button
                  onClick={() => setRegistryNumber('')}
                  className="absolute inset-y-0 right-0 pr-3 flex items-center text-gray-400 hover:text-gray-600 transition-colors"
                >
                  <X className="h-5 w-5" />
                </button>
              )}
            </div>
            <button
              onClick={handleSearch}
              disabled={isSearching || !registryNumber.trim()}
              className="px-6 py-2 bg-gradient-to-r from-black to-gray-800 hover:from-gray-800 hover:to-black text-white rounded-lg font-medium focus:outline-none focus:ring-2 focus:ring-orange-500 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-200"
            >
              {isSearching ? (
                <div className="flex items-center">
                  <div className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent mr-2"></div>
                  Buscando...
                </div>
              ) : (
                <div className="flex items-center">
                  <Search className="h-4 w-4 mr-2" />
                  Buscar
                </div>
              )}
            </button>
          </div>
        </div>
      </div>

      {/* 2. MAIN CONTENT AREA */}
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">

        {/* Error State */}
        {businessNotFound && (
          <div className="bg-red-50 border border-red-200 rounded-lg p-6 mb-8">
            <div className="flex items-center">
              <Building className="h-6 w-6 text-red-500 mr-3" />
              <div>
                <h3 className="text-lg font-semibold text-red-800">
                  ESTE NEGOCIO NO ESTÁ REGISTRADO
                </h3>
                <p className="text-red-600 mt-1">
                  El número de registro "{registryNumber}" no se encontró en la base de datos.
                </p>
              </div>
            </div>
          </div>
        )}

        {/* Empty State */}
        {!searchedBusiness && !businessNotFound && (
          <div className="text-center py-16">
            <Building className="h-16 w-16 text-orange-300 mx-auto mb-4" />
            <h3 className="text-xl font-medium text-gray-700 mb-2">
              Gestión de Contenido de Negocios
            </h3>
            <p className="text-gray-500 max-w-md mx-auto">
              Ingrese el número de registro de un negocio para ver y gestionar su contenido,
              incluyendo productos, servicios, empleados y eventos.
            </p>
          </div>
        )}

        {/* Business Data Loaded */}
        {searchedBusiness && (
          <div className="space-y-8">

            {/* 3. BUSINESS PROFILE CARD */}
            <div className="bg-white rounded-2xl shadow-xl border border-orange-200 p-6 relative">
              <div className="flex flex-col md:flex-row items-start md:items-center gap-6 mb-6">

                {/* Logo Upload */}
                <div className="relative group shrink-0">
                  <div className="h-24 w-24 rounded-full border-4 border-orange-100 shadow-md overflow-hidden bg-gray-50 flex items-center justify-center relative">
                    {logoUrl ? (
                      <img src={logoUrl} alt="Logo" className="h-full w-full object-cover" />
                    ) : (
                      <Building className="h-10 w-10 text-gray-300" />
                    )}
                    {isUploadingImage && (
                      <div className="absolute inset-0 bg-black/50 flex items-center justify-center z-10">
                        <Loader2 className="h-6 w-6 text-white animate-spin" />
                      </div>
                    )}
                  </div>
                  <button
                    onClick={triggerFileInput}
                    disabled={isUploadingImage}
                    className="absolute bottom-0 right-0 bg-orange-600 hover:bg-orange-700 text-white p-2 rounded-full shadow-lg transition-transform hover:scale-110 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    <Camera className="h-4 w-4" />
                  </button>
                  <input
                    type="file"
                    ref={fileInputRef}
                    className="hidden"
                    accept="image/png, image/jpeg, image/jpg"
                    onChange={handleImageChange}
                  />
                </div>

                {/* Info Text */}
                <div className="flex-1">
                  <h2 className="text-2xl font-bold text-gray-800 flex items-center">
                    <Building className="h-6 w-6 text-orange-500 mr-3 md:hidden" />
                    {searchedBusiness.business_name}
                  </h2>
                  <p className="text-sm text-gray-500 mt-1">{searchedBusiness.business_registry_number}</p>
                </div>
              </div>

              {/* Grid Info */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="space-y-4">
                  <div className="flex items-center">
                    <Phone className="h-5 w-5 text-orange-500 mr-3" />
                    <span className="text-gray-700">{searchedBusiness.business_phone || 'No disponible'}</span>
                  </div>
                  <div className="flex items-center">
                    <User className="h-5 w-5 text-orange-500 mr-3" />
                    <span className="text-gray-700">{searchedBusiness.business_owner_name || 'No disponible'}</span>
                  </div>
                  <div className="flex items-center">
                    <MapPin className="h-5 w-5 text-orange-500 mr-3" />
                    <span className="text-gray-700">{searchedBusiness.business_address || 'No disponible'}</span>
                  </div>
                </div>
                <div className="space-y-4">
                  <div className="flex items-center">
                    <Mail className="h-5 w-5 text-orange-500 mr-3" />
                    <a href={`mailto:${searchedBusiness.business_email}`} className="text-orange-600 hover:text-orange-800">
                      {searchedBusiness.business_email || 'No disponible'}
                    </a>
                  </div>
                  <div className="flex items-center">
                    <Calendar className="h-5 w-5 text-orange-500 mr-3" />
                    <span className="text-gray-700">
                      {searchedBusiness.business_creation_date
                        ? `Registrado: ${new Date(searchedBusiness.business_creation_date).toLocaleDateString('es-PR')}`
                        : 'Fecha no disponible'}
                    </span>
                  </div>
                  <div className="flex items-center">
                    <Tag className="h-5 w-5 text-orange-500 mr-3" />
                    <span className="text-gray-700">{searchedBusiness.business_category || 'Sin categoría'}</span>
                  </div>
                </div>
              </div>
            </div>

            {/* 4. CONTENT SECTIONS GRID (Navigation) */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {renderSectionHeader(
                <Images className="h-5 w-5 text-orange-500" />,
                'Galería de Imágenes',
                0,
                'gallery',
                true
              )}
              {renderSectionHeader(
                <Package className="h-5 w-5 text-orange-500" />,
                'Productos Registrados',
                getSectionCount(businessProducts),
                'products'
              )}
              {renderSectionHeader(
                <Wrench className="h-5 w-5 text-orange-500" />,
                'Servicios Registrados',
                getSectionCount(businessServices),
                'services'
              )}
              {renderSectionHeader(
                <Users className="h-5 w-5 text-orange-500" />,
                'Empleados Registrados',
                getSectionCount(businessEmployees),
                'employees'
              )}
              {renderSectionHeader(
                <PartyPopper className="h-5 w-5 text-orange-500" />,
                'Eventos de Negocio',
                getSectionCount(businessEvents),
                'events'
              )}
            </div>

            {/* 5. EXPANDABLE SECTION CONTENT */}

            {/* Gallery Section */}
            {activeSection === 'gallery' && (
              <BusinessGallerySection
                searchedBusiness={searchedBusiness}
                businessGalleryContext={businessGallery}
                adminId={adminId}
                createTracker={createTracker}
              />
            )}

            {/* Products Section */}
            {activeSection === 'products' && (
              <BusinessProductsSection
                products={businessProducts}
                businessProductContext={businessProduct}
              />
            )}

            {/* Services Section */}
            {activeSection === 'services' && (
              <BusinessServicesSection
                services={businessServices}
                businessServiceContext={businessService}
              />
            )}

            {/* Employees Section */}
            {activeSection === 'employees' && (
              <BusinessEmployeesSection
                employees={businessEmployees}
                businessEmployeeContext={businessEmployee}
              />
            )}

            {/* Events Section */}
            {activeSection === 'events' && (
              <BusinessEventsSection
                events={businessEvents}
                searchedBusiness={searchedBusiness}
                businessEventContext={businessEvent}
                adminId={adminId}
                createTracker={createTracker}
                onEventsUpdate={() => loadBusinessData(searchedBusiness.business_registry_number)}
                eventImagesMap={eventImagesMap}
                onManageEventImages={openEventImageDialog}
              />
            )}

          </div>
        )}
      </main>

      {/* Event Image Management Dialog */}
      {showEventImageDialog && selectedEventForImages && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl shadow-2xl max-w-4xl w-full max-h-[90vh] overflow-hidden">
            {/* Header */}
            <div className="bg-gradient-to-r from-orange-500 to-orange-600 p-6 text-white">
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-3">
                  <ImagePlus className="h-6 w-6" />
                  <div>
                    <h3 className="text-xl font-bold">Gestión de Imágenes del Evento</h3>
                    <p className="text-orange-100 text-sm mt-1">{selectedEventForImages.event_name}</p>
                  </div>
                </div>
                <button
                  onClick={closeEventImageDialog}
                  className="p-2 hover:bg-orange-600 rounded-lg transition-colors"
                >
                  <X className="h-6 w-6" />
                </button>
              </div>
            </div>

            {/* Content */}
            <div className="p-6 overflow-y-auto max-h-[calc(90vh-180px)]">
              {/* Upload Section */}
              <div className="mb-6">
                <div className="border-2 border-dashed border-orange-300 rounded-lg p-6 text-center hover:border-orange-500 transition-colors">
                  <input
                    type="file"
                    ref={eventImageInputRef}
                    className="hidden"
                    accept="image/jpeg, image/jpg, image/png, image/gif, image/webp"
                    onChange={handleEventImageUpload}
                  />
                  <Upload className="h-12 w-12 text-orange-400 mx-auto mb-3" />
                  <button
                    onClick={() => eventImageInputRef.current?.click()}
                    className="text-orange-600 hover:text-orange-700 font-medium"
                  >
                    Haga clic para subir una imagen
                  </button>
                  <p className="text-sm text-gray-500 mt-2">
                    JPEG, PNG, GIF, WebP - Máximo 15MB
                  </p>
                </div>
              </div>

              {/* Images Grid */}
              {isLoadingEventImages ? (
                <div className="text-center py-12">
                  <Loader2 className="h-8 w-8 text-orange-500 animate-spin mx-auto mb-3" />
                  <p className="text-gray-500">Cargando imágenes...</p>
                </div>
              ) : currentEventImages.length === 0 ? (
                <div className="text-center py-12">
                  <Images className="h-16 w-16 text-gray-300 mx-auto mb-4" />
                  <p className="text-gray-500">No hay imágenes para este evento</p>
                  <p className="text-sm text-gray-400 mt-1">Suba su primera imagen usando el botón de arriba</p>
                </div>
              ) : (
                <div>
                  <div className="mb-4">
                    <p className="text-sm text-gray-600">
                      <strong>{currentEventImages.length}</strong> imagen(es) - Arrastre para reordenar
                    </p>
                  </div>
                  <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                    {currentEventImages.map((image, index) => (
                      <div
                        key={image.cloudflare_image_id}
                        draggable
                        onDragStart={() => handleDragStart(image.cloudflare_image_id)}
                        onDragOver={handleDragOver}
                        onDrop={(e) => handleDrop(e, image.cloudflare_image_id)}
                        className={`relative group rounded-lg overflow-hidden border-2 ${index === 0 ? 'border-orange-500' : 'border-gray-200'
                          } hover:border-orange-400 transition-all cursor-move ${draggedImageId === image.cloudflare_image_id ? 'opacity-50' : ''
                          }`}
                      >
                        {/* Primary Badge */}
                        {index === 0 && (
                          <div className="absolute top-2 left-2 bg-orange-500 text-white px-2 py-1 rounded-md text-xs font-medium flex items-center space-x-1 z-10">
                            <Check className="h-3 w-3" />
                            <span>Principal</span>
                          </div>
                        )}

                        {/* Drag Handle */}
                        <div className="absolute top-2 right-2 bg-white/90 p-1 rounded opacity-0 group-hover:opacity-100 transition-opacity z-10">
                          <GripVertical className="h-4 w-4 text-gray-600" />
                        </div>

                        {/* Image */}
                        <div className="aspect-square bg-gray-100">
                          <img
                            src={image.cloudflare_image_url}
                            alt={`Event ${index + 1}`}
                            className="w-full h-full object-cover"
                          />
                        </div>

                        {/* Actions Overlay */}
                        <div className="absolute inset-0 bg-black/0 group-hover:bg-black/60 transition-all flex items-center justify-center opacity-0 group-hover:opacity-100">
                          <div className="flex space-x-2">
                            {index !== 0 && (
                              <button
                                onClick={() => handleSetAsPrimary(image.cloudflare_image_id)}
                                className="p-2 bg-orange-500 hover:bg-orange-600 text-white rounded-lg transition-colors"
                                title="Establecer como principal"
                              >
                                <Check className="h-4 w-4" />
                              </button>
                            )}
                            <button
                              onClick={() => handleEventImageDelete(image.cloudflare_image_id)}
                              className="p-2 bg-red-500 hover:bg-red-600 text-white rounded-lg transition-colors"
                              title="Eliminar imagen"
                            >
                              <Trash2 className="h-4 w-4" />
                            </button>
                          </div>
                        </div>

                        {/* Image Info */}
                        <div className="p-2 bg-white">
                          <p className="text-xs text-gray-500 truncate">
                            Orden: {image.image_order || index + 1}
                          </p>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>

            {/* Footer */}
            <div className="border-t border-gray-200 p-4 bg-gray-50">
              <button
                onClick={closeEventImageDialog}
                className="w-full px-4 py-2 bg-gray-200 hover:bg-gray-300 text-gray-800 rounded-lg font-medium transition-colors"
              >
                Cerrar
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default BusinessContent;